import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Hero.css';

gsap.registerPlugin(ScrollTrigger);

export default function Hero() {
  const containerRef = useRef(null);
  const textRef1 = useRef(null);
  const textRef2 = useRef(null);
  const imageWrapperRef = useRef(null); // Controls scroll parallax
  const imageRef = useRef(null); // Just the image reference
  const uiBottomLeftRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {

    // On attend que l'image soit décodée avant de lancer l'animation pour éviter le lag
    const img = imageRef.current;
    const startAnimation = () => {
      const isMobile = window.innerWidth <= 768;
      const tl = gsap.timeline({ delay: isMobile ? 0.3 : 0.1 });
      
      tl.fromTo([textRef1.current, textRef2.current], 
        { y: 60, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1.2, ease: "power4.out" }
      )
      .fromTo(imageWrapperRef.current,
        { y: 50, opacity: 0, scale: isMobile ? 1.05 : 1.1 },
        { y: 0, opacity: 1, scale: isMobile ? 1.25 : 1.5, duration: 1.4, ease: "power3.out" },
        "-=1.0"
      )
      .fromTo(uiBottomLeftRef.current,
        isMobile ? { y: 20, opacity: 0 } : { x: -60, opacity: 0 },
        { x: 0, y: 0, opacity: 1, duration: 1.2, ease: "power3.out" },
        "-=0.9"
      );
    };

    if (img && img.complete) {
      img.decode().then(startAnimation).catch(startAnimation);
    } else if (img) {
      img.onload = () => img.decode().then(startAnimation).catch(startAnimation);
    } else {
      startAnimation();
    }

    gsap.to([textRef1.current, textRef2.current], {
      y: -150, 
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1
      }
    });

    gsap.to(imageWrapperRef.current, {
      y: -500, 
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom top",
        scrub: 1
      }
    });

    gsap.fromTo(uiBottomLeftRef.current,
      { x: 0, opacity: 1 },
      { x: '-100vw', opacity: 0,
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top top",
          end: "30% top",
          scrub: 1
        }
      }
    );

    }, containerRef); // end gsap.context

    return () => ctx.revert();
  }, []);

  return (
    <section className="hero-modern" ref={containerRef}>
      <div className="hero-modern__bg"></div>
      
      {/* Hidden H1 for SEO - Experts priority 1 */}
      <h1 className="visually-hidden">Luxury Wood — Menuiserie Sur Mesure Haut de Gamme au Maroc</h1>
      
      {/* Text BEHIND the image (z-index: 1) */}
      <div className="hero-modern__typography hero-modern__typography--bg" ref={textRef1}>
        <div className="hero-text-row">LUXURY</div>
        <div className="hero-text-row">WOOD</div>
        <div className="hero-text-row hero-text-row--hidden">CONCEPT</div>
      </div>

      {/* Centerpiece Image (z-index: 2) - Tucked between WOOD and CONCEPT */}
      <div className="hero-modern__image-wrapper" ref={imageWrapperRef}>
        <img 
          src="/images/hero-transparent.webp" 
          alt="Menuiserie de luxe" 
          className="hero-modern__image" 
          ref={imageRef} 
          fetchPriority="high"
          loading="eager"
          decoding="async"
        />
      </div>

      {/* Text IN FRONT of the image (z-index: 3) - Acts as a physical mask over the image */}
      <div className="hero-modern__typography hero-modern__typography--fg" ref={textRef2}>
        <div className="hero-text-row hero-text-row--hidden">LUXURY</div>
        <div className="hero-text-row hero-text-row--hidden">WOOD</div>
        <div className="hero-text-row">CONCEPT</div>
      </div>

      <div className="hero-ui-bottom-left" ref={uiBottomLeftRef}>
        <div className="hero-modern__label">
          <span className="hero-modern__gold-dot"></span> MENUISERIE HAUT DE GAMME
        </div>
      </div>
      
      {/* Scroll indicator */}
      <div className="hero__scroll-indicator">
        <div className="hero__scroll-line" />
        <span>Scroll</span>
      </div>
    </section>
  );
}
